import { Position } from '@xyflow/react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router';
import { Cog6ToothIcon, XMarkIcon, CubeTransparentIcon } from '@heroicons/react/24/outline';
import InputOutputPorts from '@/components/nodes/InputOutputPorts';
import { memo } from 'react';
import { pathwayActions } from '@/stores/pathwayStore';
import { useCaseId } from '@/stores/caseStore';
import { settingsActions } from '@/stores/settingsStore';
import featureConfig from '@/featureConfig';

const PathwayNode = ({ data: node }) => {
  const { id, inputs, outputs, label, internalPathwayId, mirrored } = node;
  const inputPosition = mirrored ? Position.Right : Position.Left;
  const outputPosition = mirrored ? Position.Left : Position.Right;
  const { projectId, pathwayId, nodeId } = useParams();
  const caseId = useCaseId();
  const navigate = useNavigate();
  const removeNode = e => {
    e.stopPropagation();
    pathwayActions.removeNode(node.id);
    if (nodeId === id) {
      navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}`);
    }
  };

  const handleNodeClick = () => {
    settingsActions.closePalette();
    navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}/nodes/${id}`, { state: { node } });
  };


  return (
    <div
      className="group/item relative bg-white border border-gray-700 w-112 rounded-lg shadow-lg hover:border-gray-900"
      onClick={handleNodeClick}
    >
      {internalPathwayId && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          <div className="relative group/internals pointer-events-auto">
            <CubeTransparentIcon className="w-14 h-14 text-blue-600" />
            <span className="absolute bottom-[calc(100%+0.25rem)] left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover/internals:opacity-100 transition-opacity duration-100 pointer-events-none">
              This module has an internal subpathway
            </span>
          </div>
        </div>
      )}
      <div className="flex">
        {featureConfig.manageModules && (
          <div className="group/edit invisible group-hover/item:visible">
            <div onClick={removeNode} className="nodrag pt-2 pl-2 pb-4 pr-4 cursor-pointer">
              <XMarkIcon className="h-6 w-6 text-gray-400 hover:text-black" />
            </div>
          </div>
        )}
        <div className="flex-1">
          <p className="p-2 mt-4 mb-4 text-xl font-semibold text-center">{label}</p>
        </div>
        <div className="flex m-2 justify-end group/edit invisible group-hover/item:visible">
          <Cog6ToothIcon className="w-6 h-6 text-gray-400 hover:text-black cursor-pointer" />
        </div>
      </div>
      <div>
        <div className="flex">
          <InputOutputPorts
            parentId={id}
            nodes={mirrored ? outputs : inputs}
            label={mirrored ? 'Outputs' : 'Inputs'}
            handleType={mirrored ? 'output' : 'input'}
            handlePosition={mirrored ? outputPosition : inputPosition}
          />
          <InputOutputPorts
            parentId={id}
            nodes={mirrored ? inputs : outputs}
            label={mirrored ? 'Inputs' : 'Outputs'}
            handleType={mirrored ? 'input' : 'output'}
            handlePosition={mirrored ? inputPosition : outputPosition}
          />
        </div>
      </div>
    </div>
  );
};

PathwayNode.propTypes = {
  data: PropTypes.object,
};

const MemoizedPathwayNode = memo(PathwayNode);

export default MemoizedPathwayNode;
